// react and external-libs
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
// MUI
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { AccessTimeOutlined, Add, Cancel, CheckCircle, Close, Delete, DeleteOutlineOutlined, EditOutlined, MoreVert } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
// redux
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { QuestionnaireType, fetchPublishedQuestionnaire } from '../../../../../app/store/surveySlice';
// interface
import { Questionnaire } from '../../../../../interfaces/questionnaire.interface';
import { Survey } from '../../../../../interfaces/survey.interface';
import { Workflow, WorkflowTypeKey } from '../../../../../interfaces/workflow.interface';
import { AssessmentSurvey } from '../../../../../interfaces/assessment.interface';
import User, {
  Application,
  ApplicationVersion,
  Badge,
  Company,
  CompanyApplication,
  CompanyCertification,
  Version,
} from '../../../../../interfaces/users.interface';
import { fetchAll } from '../../../../../app/store/questionnaireSlice';
import { canStartFiveThreeOneSurvey } from '../../../../../services/adminUtils';
import StartNewQuestionnaireModal from '../../../StartNewQuestionnaireModal';
// service
import { WorkflowStatus as WorkflowStatusInterface, createNewWorkflow, fetchWorkflows, workflowLabels, workflowVersion, WORKFLOW_MAP, handleDeativateWorkflows } from '../../../../../services/workflow';
import { fetchGoldBadgeLabel, fetchBlueBadgeLabel, recentBadgeByType, BLUE_BADGE_LABEL, GOLD_BADGE_LABEL, SILVER_BADGE_LABEL, GOLD_NEXT_GEN_BADGE_LABEL, GOLD_STAR_BADGE_LABEL, selectBadgeFromArray } from '../../../../../services/badge';
import createSurvey, { fetchSurvey } from '../../../../../services/surveys';
import { fetchAssessmentSurvey, updateAssessmentSurvey } from '../../../../../services/assessments';
// components
import GetBadge from '../../../GetBadge';
import WorkflowStatus from '../../../WorkflowStatus';
import WorkflowButtons from '../../../WorkflowButtons';
import AssessmentForm from '../../../../Assessments/NewAssessmentModal';
import { updateWorkflow } from '../../../../../app/store/workflowSlice';
import StandardDialog, { StandardDialogActions } from '../../../../Modals/StandardDialog';
import { HardeningDocument } from '../../../../../interfaces/hardeningDocuments';
import { QuestionnaireTypes, QuestionnaireVersions } from '../../../../../services/questionnaires';
import { getQuestionnaireModalData, StartQuestionnaireModalData } from '../../../../../services/surveyHelpers';
import { USER_TYPES } from '../../../../../services/user';
import { useCanAddNewWorkflow } from '../../../../../hooks/useCanAddNewWorkflow';
import NewCustomApp from '../../ApplicationModals/NewCustomApp';
import { deleteCompanyApplication } from '../../../../../services/companyApplication';
import { getHighestRankAssessmentBadgeLabel } from '../../../../../utils/reportOutput';

type VersionSidebarProps = {
  isOpen: boolean;
  company: Company;
  companyApp?: CompanyApplication;
  companySuccessCallback?: (company: Company, companyApp: CompanyApplication, type: 'edit' | 'delete') => void;
  application?: Application;
  version?: Version;
  isAssessment?: boolean;
  showBeginNextWorkflow?: boolean;
  userCanAdminCompany?: boolean;
  onClose: () => void;
};

const VersionSidebarV2 = (props: VersionSidebarProps) => {
  const { isOpen, company, companyApp, application, version, isAssessment = false, onClose, showBeginNextWorkflow, companySuccessCallback, userCanAdminCompany = false } = props;
  // redux and router
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.user);
  const questionnaires: Questionnaire[] = useAppSelector(state => state.questionnaires.results);
  const { applicationVersions: avs } = useAppSelector((state) => state.serviceCategories);
  const { application_baseline: appBaseline } = useAppSelector((state) => state.surveys);
  const { workflows: fullWf } = useAppSelector((state) => state.workflow);
  const { documents } = useAppSelector((state) => state.serviceCategories);
  // states
  const [isCreatingWorkflow, setIsCreatingWorkflow] = useState(false);
  const isCreatingWorkflowRef = useRef(false);
  const [workflowMenuAnchorEl, setWorkflowMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isAssessmentModalOpen, setIsAssessmentModalOpen] = useState(false);
  const [surveys, setSurveys] = useState<Survey[] | undefined>(undefined);
  const [assessmentSurveys, setAssessmentSurveys] = useState<AssessmentSurvey[] | undefined>(undefined);
  const [wfToDeactivate, setwfToDeactivate] = useState<undefined | Workflow>(undefined);
  const [inactiveWorkflows, setInactiveWorkflows] = useState<Workflow[]>([]);
  const [isStartQuestionnaireModalOpen, setIsStartQuestionnaireModalOpen] = useState(false);
  const [startQuestionnaireModalData, setStartQuestionnaireModalData] = useState<StartQuestionnaireModalData | null>(null);
  const [workflowToDeactivate, setWorkflowToDeactivate] = useState<undefined | Workflow>(undefined);
  const buttonRef = React.useRef(null);
  // local variables
  const canStart531 = useMemo(() => canStartFiveThreeOneSurvey(user), [user]);

  const userUnpublishedQuestionnaires = useMemo(() => {
    return questionnaires.filter(q => q.isPublished === false && canStart531 && q.version === QuestionnaireVersions.V_5_3_1);
  }, [canStart531, questionnaires]);

  const targetVersion = useMemo(() => (
    canStart531
      ? userUnpublishedQuestionnaires[0]?.version
      : questionnaires.find(q => q.isPublished)?.version
  ), [
    canStart531,
    userUnpublishedQuestionnaires?.[0]?.version,
    questionnaires?.find(q => q.isPublished)?.version,
  ]);

  const CURRENT_TPN_WORKFLOW_TYPE = useMemo(() => Object.entries(WORKFLOW_MAP).find(
    ([, value]) => value.questionnaireVersion === targetVersion,
  )?.[0] as WorkflowTypeKey, [targetVersion]);


  const workflows = useMemo(
    () =>
      fullWf?.filter(
        (wf) =>
          wf.application &&
          wf.version &&
          version &&
          application &&
          (wf.application as Application).id === application.id &&
          (wf.version as Version).id === version.id &&
          wf.isActive,
      ) || [],
    [fullWf, version, application],
  );
  const isWorkflowMenuOpen = Boolean(workflowMenuAnchorEl);
  const wfList = useMemo(() => {
    if (workflows) return [...workflows, ...inactiveWorkflows].sort((a, b) => b.id - a.id);
    return [...inactiveWorkflows].sort((a, b) => b.id - a.id);
  }, [workflows, inactiveWorkflows]);
  const siteNames = useMemo(() => companyApp?.sites?.map((site) => site.name), [companyApp?.sites]);
  const serviceNames = useMemo(() => companyApp?.services?.map((service) => service.name), [companyApp?.services]);
  const [editApplicationOpen, setEditApplicationOpen] = useState(false);
  const [deleteApplicationOpen, setDeleteApplicationOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // short/simple functions
  const handleMenuClose = useCallback(() => setAnchorEl(null), []);
  const handleEditApplicationClose = useCallback(() => setEditApplicationOpen(false), []);
  const handleDeleteApplicationClose = useCallback(() => setDeleteApplicationOpen(false), []);
  const handleMenuClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget), []);
  const isMenuOpen = Boolean(anchorEl);
  const docs = useMemo(() => {
    if (!application?.hardeningDocuments?.length || !version) return [];
    const curDocs = (application.hardeningDocuments as HardeningDocument[]).filter(
      (hd) => hd.version === version.value,
    );
    if (!curDocs) return [];
    return curDocs;
  }, [application?.hardeningDocuments, version?.value]);

  const drawerStyles = useMemo(
    () => (theme: Theme) => ({
      '& .MuiPaper-root': {
        width: 600,
        backgroundColor: '#F3F5FB',
        [theme.breakpoints.down('sm')]: {
          width: 250,
        },
      },
    }),
    [],
  );
  // short/simple functions
  const handleWorkflowMenuClose = useCallback(() => setWorkflowMenuAnchorEl(null), []);
  const appWorkflows = useMemo(() => (
    workflows?.filter((wf) => wf.application === application?.id || (wf.application as Application)?.id === application?.id) || []
  ), [workflows, application]);

  const handleWorkflowClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setWorkflowMenuAnchorEl(e.currentTarget);
  }, []);

  // v2 renders from the server-paginated apps endpoint; changes notify the parent
  // (which re-fetches the page) rather than rebuilding the full application list.
  const handleDeleteApplicationSubmit = useCallback(async () => {
    if (!companyApp) return;

    await deleteCompanyApplication(companyApp);
    if (companySuccessCallback) companySuccessCallback(company, companyApp, 'delete');
    handleDeleteApplicationClose();
  }, [companyApp, company, companySuccessCallback, handleDeleteApplicationClose]);

  const handleCreateWorkflow = useCallback(
    async (type: WorkflowTypeKey) => {
      if (isCreatingWorkflowRef.current) return; // Prevent multiple simultaneous creations

      try {
        isCreatingWorkflowRef.current = true;
        setIsCreatingWorkflow(true);
        setWorkflowMenuAnchorEl(null);
        let finalBaseline = appBaseline;  // Default published baseline
        if (!finalBaseline) {
          const res = await dispatch(fetchPublishedQuestionnaire(QuestionnaireTypes.APPLICATION_BASELINE)).unwrap();
          finalBaseline = (res as QuestionnaireType).questionnaire.id;
        }

        const filteredQuestionnaires = questionnaires.filter(q => q.type === 'application_baseline'); // Includes unpublished questionnaires
        if (canStart531 && !!filteredQuestionnaires.length) {
          finalBaseline = filteredQuestionnaires.sort((a, b) =>  b.id - a.id)[0].id;
        }

        const baselineSurvey = await createSurvey({
          questionnaire: finalBaseline as number,
          status: 'incomplete',
          company: company.id!,
          questionAnswers: [],
          application: application?.id,
          version: version?.value!,
        });
        if (!baselineSurvey) return;

        await createNewWorkflow({
          workflowType: type,
          baseline: baselineSurvey.id,
          status: 'incomplete',
          serviceProvider: company.id!,
          application: application?.id,
          version: version?.id,
        });
        navigate(`/questionnaire/${baselineSurvey.id!}/`, { replace: true });
      } catch (error) {
        console.error('creating workflow failed', error);
      } finally {
        isCreatingWorkflowRef.current = false;
        setIsCreatingWorkflow(false);
      }
    },
    [appBaseline, company.id, application?.id, version, dispatch, navigate, questionnaires, canStart531, isCreatingWorkflowRef],
  );

  const handleStartQuestionnaire = useCallback( async (type: WorkflowTypeKey) => {
    if (!application?.id || isCreatingWorkflowRef.current) {
      return;
    }

    if (!workflows || workflows.length === 0) {
      handleCreateWorkflow(type);
      return;
    }

    const recentApplicationWorkflow = appWorkflows.sort((a, b) => moment(b.createdAt).unix() - moment(a.createdAt).unix())[0];

    if (!recentApplicationWorkflow) {
      return;
    }

    const modalData = getQuestionnaireModalData(
      recentApplicationWorkflow.status as WorkflowStatusInterface,
      workflowVersion(recentApplicationWorkflow.workflowType),
      workflowVersion(type),
      assessmentSurveys?.some((s) => s.id === recentApplicationWorkflow.assessmentSurvey && (!!s.bestPracticeRemediationCount || !!s.implementationGuidanceRemediationCount)),
    );

    if (!modalData.data) {
      // Skip modal, deactivate all other workflows and start new workflow
      await handleDeativateWorkflows(appWorkflows);
      handleCreateWorkflow(type);
      return;
    }
    setWorkflowToDeactivate(recentApplicationWorkflow);
    setStartQuestionnaireModalData(modalData);
    setIsStartQuestionnaireModalOpen(true);
  }, [workflows, application, assessmentSurveys, handleCreateWorkflow, appWorkflows, isCreatingWorkflowRef]);

  const handleCloseStartQuestionnaireModal = useCallback(async (startWf: boolean) => {
    if (!startWf) {
      setIsStartQuestionnaireModalOpen(false);
      return;
    }

    // Deactivate all other workflows and start new workflow
    try {
      await handleDeativateWorkflows(appWorkflows);
      handleCreateWorkflow(CURRENT_TPN_WORKFLOW_TYPE);
    } catch (error) {
      console.error('Failed to deactivate workflow:', error);
    } finally {
      setIsStartQuestionnaireModalOpen(false);
      setWorkflowToDeactivate(undefined);
    }
  }, [workflowToDeactivate, appWorkflows, CURRENT_TPN_WORKFLOW_TYPE]);

  // Certs are embedded on the company application by the apps endpoint.
  const companyCerts = useMemo(() => {
    const appCerts = companyApp?.certifications;
    if (!avs.length || !appCerts || appCerts.length === 0 || !isOpen)
      return undefined;
    return appCerts.filter(
      (cc) =>
        cc.applicationVersions &&
        cc.applicationVersions.some((av) => {
          const CURAVS = avs.find((cav) => cav.id === av);
          if (!CURAVS) return;
          return (CURAVS.version as Version).id === version?.id;
        }),
    );
  }, [avs, companyApp?.certifications, version, isOpen]);

  const applicationVersions = useMemo(
    () => (companyCertification: CompanyCertification) => {
      if (
        !avs.length ||
        !companyCertification.applicationVersions ||
        companyCertification.applicationVersions.length === 0
      )
        return undefined;
      const newAVS = avs.filter(
        (av) => companyCertification.applicationVersions!.findIndex((ccav) => ccav === av.id) > -1,
      );
      if (!newAVS?.length) return undefined;
      return [...new Set(newAVS)];
    },
    [avs],
  );

  const canAddNewWorkflow = useCanAddNewWorkflow(workflows);

  const findSurvey = useMemo(
    () => (workflow: Workflow) =>
      surveys?.find((sur) => (workflow.bpSurvey ? sur.id === workflow.bpSurvey : sur.id === workflow.baseline)),
    [surveys],
  );

  const findAssessmentSurvey = useMemo(
    () => (workflow: Workflow) => assessmentSurveys?.find((as) => as.id === workflow.assessmentSurvey),
    [assessmentSurveys],
  );

  const handleFetchSurvey = useCallback(async (surveyId: number) => {
    const sur = await fetchSurvey(surveyId);
    setSurveys((cur) => {
      if (cur) return [...cur, sur];
      if (!cur) return [sur];
    });
  }, []);

  const handleFetchAssessmentSurvey = useCallback(async (assessmentSurveyId: number) => {
    const assSurvey = await fetchAssessmentSurvey(assessmentSurveyId);
    setAssessmentSurveys((cur) => {
      if (cur) return [...cur, assSurvey];
      if (!cur) return [assSurvey];
    });
  }, []);

  const deactivateWorkflow = () => {
    if (!wfToDeactivate) return;
    dispatch(updateWorkflow({ id: wfToDeactivate.id, isActive: false }));
    setwfToDeactivate(undefined);
  };

  const canDeactivate = (wf: Workflow) => user?.type === USER_TYPES.TPN_ADMIN && wf.isActive;
  const updateAssSur = (p: { assSur?:AssessmentSurvey, key:string, value: boolean }) => p.assSur && updateAssessmentSurvey({ ...p.assSur, [p.key]: p.value });

  const handleDocClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, id?: number) => {
      e.stopPropagation();
      if (!id) return;
      const doc = documents.find((cdoc) => cdoc.id === id);
      if (!doc) return;

      const link = document.createElement('a');
      link.setAttribute('href', doc.document);
      link.setAttribute('target', '_blank');
      link.click();
    },
    [documents],
  );

  const handleDeleteApplicationOpen = useCallback(() => {
    handleMenuClose();
    setDeleteApplicationOpen(true);
  }, []);

  const handleEditApplicationOpen = useCallback(() => {
    handleMenuClose();
    setEditApplicationOpen(true);
  }, []);

  const handleEditApplicationSubmit = useCallback(
    (companyApplicationUpdated: CompanyApplication) => {
      if (companySuccessCallback) companySuccessCallback(company, companyApplicationUpdated, 'edit');
      handleEditApplicationClose();
    },
    [company, companySuccessCallback, handleEditApplicationClose],
  );

  useEffect(() => {
    dispatch(fetchAll({ unpublished: false }));
  }, []);

  useEffect(() => {
    if (!wfList.length) return;
    wfList.forEach((workflow) => {
      if (workflow?.assessmentSurvey) {
        // fetch assessment survey
        if (!assessmentSurveys?.find((sas) => sas.id === workflow?.assessmentSurvey)) {
          handleFetchAssessmentSurvey(workflow?.assessmentSurvey);
        }
        return;
      }

      // we have a bp survey and no assessment
      if (
        workflow?.bpSurvey &&
        !workflow?.assessment &&
        !['submittedForAssessment', 'complete'].includes(workflow?.status)
      ) {
        // fetch survey
        if (!surveys?.find((ss) => ss.id === workflow?.bpSurvey)) {
          handleFetchSurvey(workflow?.bpSurvey);
        }
        return;
      }
    });
  }, [wfList]);

  useEffect(() => {
    if (!version) return;
    setInactiveWorkflows([]);
    fetchWorkflows(
      {
        application: application?.id,
        version: version?.id,
        service_provider: company.id,
      },
      false,
    ).then(setInactiveWorkflows);
  }, [version]);

  const mostRecentBlue = useMemo(() => workflows && recentBadgeByType(workflows, [BLUE_BADGE_LABEL]), [workflows]);
  const mostRecentGold = useMemo(() => workflows && recentBadgeByType(
    workflows,
    [
      GOLD_BADGE_LABEL,
      SILVER_BADGE_LABEL,
      GOLD_NEXT_GEN_BADGE_LABEL,
      GOLD_STAR_BADGE_LABEL,
    ],
  ), [workflows]);

  const renderMenuItem = useCallback(
    (surveyVersion: WorkflowTypeKey) => {
      return canAddNewWorkflow(surveyVersion) ? (
        <MenuItem onClick={() => handleStartQuestionnaire(surveyVersion)}>
          {workflowLabels(surveyVersion)}
        </MenuItem>
      ) : (
        <MenuItem disabled>No new Questionnaires</MenuItem>
      );
    },
    [canAddNewWorkflow, handleStartQuestionnaire, workflowLabels],
  );

  // Auto-opens the workflow menu when canStartNewQuestionniare is true
  useEffect(() => {
    if (user && isOpen && showBeginNextWorkflow) {
      const timer = setTimeout(() => {
        if (buttonRef.current) {
          setWorkflowMenuAnchorEl(buttonRef.current);
        }
      }, 100);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setWorkflowMenuAnchorEl(null);
    }
  }, [user, isOpen, showBeginNextWorkflow]);

  const mainGoldBadgeLabel = useMemo(() =>
    mostRecentGold.badge &&
    fetchGoldBadgeLabel([mostRecentGold.badge], mostRecentGold.version, mostRecentGold.badge.title)
  , [mostRecentGold]);

  const mainBlueBadgeLabel = useMemo(() =>
    mostRecentBlue.badge && fetchBlueBadgeLabel([mostRecentBlue.badge], mostRecentBlue.version)
  , [mostRecentBlue]);

  const noWorkflowAssessmentSurveys: boolean = useMemo(() => {
    if (!wfList || wfList.length === 0) return true;
    return wfList.every((wf) => !wf?.assessmentSurvey && wf?.status !== 'submittedForAssessment');
  }, [wfList]);

  return (
    <>
      <Drawer anchor='right' open={isOpen} onClose={onClose} sx={drawerStyles} keepMounted={false}>
        <Box
          component='section'
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: '1.25rem',
            bgcolor: '#2F2D4F',
          }}
        >
          <Typography fontSize='1.063rem' color='#fff' fontWeight='500'>
            App Version Details
          </Typography>
          <Box>
          {((userCanAdminCompany && noWorkflowAssessmentSurveys) || user?.type === USER_TYPES.TPN_ADMIN) && (
            <Tooltip title="Manage" arrow>
              <Button
                variant='contained'
                onClick={handleMenuClick}
                startIcon={<MoreVert fontSize="small" />}
              >
                Manage App
              </Button>
            </Tooltip>
          )}
            <IconButton sx={{ color: '#fff' }} onClick={onClose}>
              <Close sx={{ fontSize: '1.125rem' }} />
            </IconButton>
          </Box>
          <Menu
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleMenuClose}
              sx={{ '& .MuiMenu-paper': { minWidth: 150, color: '#737373' } }}
            >
              <MenuItem onClick={handleEditApplicationOpen} sx={{ columnGap: 1, alignItems: 'center' }}>
                <EditOutlined fontSize="small"/>
                <Typography>Edit</Typography>
              </MenuItem>
              <MenuItem onClick={handleDeleteApplicationOpen} sx={{ columnGap: 1, alignItems: 'center' }}>
                <DeleteOutlineOutlined fontSize="small"/>
                <Typography>Delete</Typography>
              </MenuItem>
            </Menu>
        </Box>
        <Box component='section' sx={{ display: 'flex', flexDirection: 'column', p: '1.25rem' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '0.25rem' }}>
              <Typography sx={{ color: '#575757', fontWeight: 600 }}>{application?.name}</Typography>-
              <Typography sx={{ color: '#575757' }}>{version?.value}</Typography>
            </Box>
            {/* Main badge section in the sidebar */}
            <GetBadge
              isActive={company.isPublished}
              shield={mostRecentGold.badge || mostRecentBlue.badge}
              goldBadgeLabel={mainGoldBadgeLabel}
              blueBadgeLabel={mainBlueBadgeLabel}
              certification={companyCerts?.at(-1)}
              company={company.id}
            />
          </Box>
        </Box>
        <Box
          component='section'
          sx={{ display: 'flex', flexDirection: 'column', p: '1.25rem', borderBottom: '1px solid #DBE3EF' }}
        >
          <Typography sx={{ color: '#575757', fontWeight: 500 }}>Sites</Typography>
          <Box sx={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', mt: '0.5rem' }}>
            {siteNames?.map((site, idx) => {
              return (
                <Chip
                  key={idx}
                  label={site}
                  size='small'
                  sx={(theme) => ({
                    bgcolor: theme.palette.chips?.site,
                    color: theme.palette.chips?.contrastText,
                    '& .MuiChip-label': { px: '1rem' },
                  })}
                />
              );
            })}
            {siteNames?.length === 0 && <Typography>-</Typography>}
          </Box>
        </Box>
        <Box
          component='section'
          sx={{ display: 'flex', flexDirection: 'column', p: '1.25rem', borderBottom: '1px solid #DBE3EF' }}
        >
          <Typography sx={{ color: '#575757', fontWeight: 500 }}>Services</Typography>
          <Box sx={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', mt: '0.5rem' }}>
            {serviceNames?.map((service, idx) => {
              return (
                <Chip
                  key={idx}
                  label={service}
                  size='small'
                  sx={(theme) => ({
                    bgcolor: theme.palette.chips?.service,
                    color: theme.palette.chips?.contrastText,
                    '& .MuiChip-label': { px: '1rem' },
                  })}
                />
              );
            })}
            {serviceNames?.length === 0 && <Typography>-</Typography>}
          </Box>
        </Box>
        <Box
          component='section'
          sx={{ display: 'flex', flexDirection: 'column', p: '1.25rem', borderBottom: '1px solid #DBE3EF' }}
        >
          <Typography sx={{ color: '#575757', fontWeight: 500 }}>Configuration Guidelines</Typography>
          <Box sx={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', mt: '0.5rem' }}>
            {docs.map((cd) => (
              <Chip
                key={cd.id}
                label={cd.type}
                size='small'
                onClick={(e) => handleDocClick(e, cd.document)}
                sx={{ bgcolor: '#DFF3D2', color: '#575757', '& .MuiChip-label': { px: '1rem' } }}
              />
            ))}
          </Box>
        </Box>
        {!isAssessment && <Box
          component='section'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: '1.25rem',
            borderBottom: '1px solid #DBE3EF',
            mt: '0.5rem',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ color: '#575757', fontWeight: 500 }}>Questionnaires</Typography>
            {user && (user.type === USER_TYPES.VENDOR_ADMIN || user.type === USER_TYPES.TPN_ADMIN) && <>
            <LoadingButton
              ref={buttonRef}
              disabled={user?.type === USER_TYPES.TPN_ADMIN}
              variant='contained'
              startIcon={<Add />}
              size='small'
              loadingPosition='start'
              aria-controls={isWorkflowMenuOpen ? 'basic-menu' : undefined}
              aria-haspopup='true'
              aria-expanded={isWorkflowMenuOpen ? 'true' : undefined}
              loading={isCreatingWorkflow}
              onClick={handleWorkflowClick}
            >
              New Questionnaire
            </LoadingButton>
            <Menu
              id='basic-menu'
              anchorEl={workflowMenuAnchorEl}
              open={isWorkflowMenuOpen}
              onClose={handleWorkflowMenuClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
            {canStart531 ? renderMenuItem(CURRENT_TPN_WORKFLOW_TYPE) : renderMenuItem(CURRENT_TPN_WORKFLOW_TYPE)}
            </Menu></>}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '0.5rem', mt: '1rem' }}>
            {wfList?.length === 0 && <Typography>-</Typography>}
            {wfList?.map((workflow, i) => {
              return (
                <Grid container key={workflow.id}>
                  {i !== 0 && <Divider />}
                  <Grid item xs={2}>
                    <GetBadge
                      isActive={company.isPublished}
                      shield={selectBadgeFromArray(workflow.badges as Badge[], true)}
                      goldBadgeLabel={fetchGoldBadgeLabel(workflow.badges as Badge[], workflowLabels(workflow.workflowType), getHighestRankAssessmentBadgeLabel(workflow.badges as Badge[]))}
                      blueBadgeLabel={fetchBlueBadgeLabel(workflow.badges as Badge[], workflowLabels(workflow.workflowType))}
                      assessmentSurvey={findAssessmentSurvey(workflow)}
                      assessor={workflow?.assignedAssessor as User | undefined}
                      certification={companyCerts?.at(-1)}
                      company={company.id}
                    />
                  </Grid>
                  <Grid item xs={canDeactivate(workflow) ? 5 : 6}>
                    <Typography sx={{ color: '#848484' }}>{workflowLabels(workflow.workflowType)}</Typography>
                    <Box sx={{ color: '#1876D2', fontWeight: 700 }}>
                      <WorkflowStatus
                        workflowDisplayType={workflow.workflowType}
                        workflowDisplayStatusObject={workflow.displayStatus}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <WorkflowButtons
                      company={company}
                      workflow={workflow}
                      survey={findSurvey(workflow)}
                      assessmentSurvey={findAssessmentSurvey(workflow)}
                    />
                  </Grid>
                  {canDeactivate(workflow) && (
                    <Grid item xs={1}>
                      <IconButton onClick={() => setwfToDeactivate(workflow)}>
                        <Delete />
                      </IconButton>
                    </Grid>
                  )}
                  {workflow.assessment && workflow.isActive && findAssessmentSurvey(workflow) && <>
                    <Grid item xs={2}></Grid>
                    <Grid item xs={5}>
                      <Stack spacing={.5} justifyContent='flex-start' alignItems='flex-start' ml='-16px' mt='8px'>
                        <Typography sx={{ fontWeight: 'bold', ml: '16px' }}>Scope</Typography>
                        <FormGroup>
                          <FormControlLabel
                            labelPlacement='start'
                            disabled={user?.type !== USER_TYPES.TPN_ADMIN}
                            control={<Switch defaultChecked={findAssessmentSurvey(workflow)?.isOnPremise} onChange={(e, v) => updateAssSur({ assSur: findAssessmentSurvey(workflow), key: 'isOnPremise', value: v })} />}
                            label={workflow?.site ? 'Site' : 'On-Prem'}
                          />
                          <FormControlLabel
                            labelPlacement='start'
                            disabled={user?.type !== USER_TYPES.TPN_ADMIN}
                            control={<Switch defaultChecked={findAssessmentSurvey(workflow)?.isCloud} onChange={(e, v) => updateAssSur({ assSur: findAssessmentSurvey(workflow), key: 'isCloud', value: v })} />}
                            label='Cloud'
                          />
                        </FormGroup>
                      </Stack>
                    </Grid>
                    <Grid item xs={5}>
                      <Stack spacing={.5} justifyContent='flex-start' alignItems='flex-start' ml='-16px' mt='8px'>
                      <Typography sx={{ fontWeight: 'bold', ml: '16px' }}>Type</Typography>
                        <FormGroup>
                          <FormControlLabel
                            labelPlacement='start'
                            disabled={user?.type !== USER_TYPES.TPN_ADMIN}
                            control={<Switch defaultChecked={findAssessmentSurvey(workflow)?.isInPerson} onChange={(e, v) => updateAssSur({ assSur: findAssessmentSurvey(workflow), key: 'isInPerson', value: v })} />}
                            label='On Site'
                          />
                        </FormGroup>
                      </Stack>
                    </Grid>
                  </>}
                </Grid>
              );
            })}
          </Box>
        </Box>}
        <Box component='section' sx={{ display: 'flex', flexDirection: 'column', p: '1.25rem' }}>
          <Typography sx={{ color: '#575757', fontWeight: 500 }}>Certificates</Typography>
          {!companyApp?.certifications ||
            (companyApp?.certifications.length === 0 && <Typography>-</Typography>)}
          {companyApp?.certifications &&
            companyApp.certifications.map((companyCert) => {
              return (
                <Box key={companyCert.id}>
                  {applicationVersions(companyCert)?.map((av: ApplicationVersion) => {
                    if ((av.version as Version).id !== version?.id) {
                      return null;
                    }

                    return (
                      <Box
                        key={(av.version as Version).id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: '0.5rem',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography
                            component={Link}
                            to={companyCert.document}
                            target='_blank'
                            rel='noreferrer'
                            sx={{ color: '#848484' }}
                          >
                            {companyCert.certification.title}
                          </Typography>
                          <Box sx={{ display: 'flex', columnGap: '0.25rem', alignItems: 'center' }}>
                            <Typography sx={{ color: '#DF8063', fontSize: '0.9rem' }}>
                              {moment(companyCert.expirationDate).format('MM/DD/YYYY')}
                            </Typography>
                            <Tooltip title='Expiry Date'>
                              <AccessTimeOutlined sx={{ color: '#808080', fontSize: '0.9rem' }} />
                            </Tooltip>
                          </Box>
                        </Box>
                        <Tooltip title={companyCert.adminApproved ? 'Accepted' : 'Rejected'}>
                          {companyCert.adminApproved ? (
                            <CheckCircle sx={{ color: '#7DBF29' }} />
                          ) : (
                            <Cancel sx={{ color: '#DF8063' }} />
                          )}
                        </Tooltip>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
        </Box>
        <AssessmentForm
          modalOpen={isAssessmentModalOpen}
          company={company}
          closeCallback={() => setIsAssessmentModalOpen(false)}
        />
        { startQuestionnaireModalData &&
          <StartNewQuestionnaireModal
            isOpen={isStartQuestionnaireModalOpen}
            handleClose={handleCloseStartQuestionnaireModal}
            startQuestionnaireModalData={startQuestionnaireModalData}
          />
        }
        <StandardDialog
          title='Deactivate Workflow'
          isOpen={!!wfToDeactivate}
          handleClose={() => setwfToDeactivate(undefined)}
        >
          <Typography>Are you sure you wish to deactivate this workflow?</Typography>
          <StandardDialogActions>
            <Button variant='outlined' onClick={() => setwfToDeactivate(undefined)}>
              Cancel
            </Button>
            <Button variant='contained' color='error' onClick={() => deactivateWorkflow()}>
              Deactivate
            </Button>
          </StandardDialogActions>
        </StandardDialog>
      </Drawer>
      <StandardDialog
        isOpen={deleteApplicationOpen}
        title={`Delete ${companyApp?.application.name} ?`}
        handleClose={handleDeleteApplicationClose}
      >
        <Typography>Are you sure you want to delete {companyApp?.application.name}?</Typography>
        <StandardDialogActions>
          <Button variant='outlined' onClick={handleDeleteApplicationClose}>
            Cancel
          </Button>
          <Button
            variant='outlined'
            color='error'
            startIcon={<Delete />}
            disabled={!userCanAdminCompany}
            onClick={handleDeleteApplicationSubmit}
          >
            Delete
          </Button>
        </StandardDialogActions>
      </StandardDialog>
      { companyApp && (
        <NewCustomApp
          isOpen={editApplicationOpen}
          company={company}
          companyApplication={companyApp}
          callBack={handleEditApplicationSubmit}
          handleClose={handleEditApplicationClose}
        />
      )}
    </>
  );
};

export default VersionSidebarV2;
